import React from 'react';
import { RowType } from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

export type Column = {
  name: string;
  width?: string;
  accessor: string;
  cell?: (
    _value: any,
    _row?: any,
    _rowType?: RowType,
    _parentItem?: any,
  ) => React.JSX.Element;
};

type TableRowsProps = {
  data: any[];
  columns: Column[];
  rowType: RowType;
  emptyState: React.ReactNode;
};

const useStyles = makeStyles()({
  cell: {
    padding: '8px 18px 8px 20px',
    borderRight: '1px solid ' + patientAccessTheme.palette.grey[300],
    borderBottom: '1px solid ' + patientAccessTheme.palette.grey[300],
    '&:last-child': {
      borderRight: 'none',
    },
  },
});

export const TableRows: React.FC<TableRowsProps> = ({
  data,
  columns,
  rowType,
  emptyState,
}) => {
  const { classes } = useStyles();

  if (data?.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={columns.length}>{emptyState}</TableCell>
      </TableRow>
    );
  }

  const renderCell = (col: Column, rowData: any, item: any) => {
    const cellContent =
      typeof col?.cell === 'function'
        ? col.cell(rowData[col.accessor], rowData, rowType, item)
        : rowData[col?.accessor] ?? '-';

    return cellContent;
  };

  const renderServiceRows = (item: any) => {
    const hasServices = Array.isArray(item.services);
    const rowsToRender = hasServices ? item.services : [item];
    const rowSpan = hasServices ? item.services.length : 1;
    const vendorColumn = columns[0];

    const vendorCellContent =
      typeof vendorColumn?.cell === 'function'
        ? vendorColumn.cell(item, item, rowType)
        : hasServices
        ? item.nameOfVendor
        : item.claims;

    return rowsToRender.map((rowData: any, index: number) => (
      <TableRow key={`${item.vendorID}-${index}`}>
        {index === 0 && (
          <TableCell
            className={classes.cell}
            rowSpan={rowSpan}
            style={{ verticalAlign: 'baseline', width: vendorColumn?.width }}
          >
            {vendorCellContent}
          </TableCell>
        )}
        {columns.slice(1).map((col, colIndex) => (
          <TableCell
            key={`${item.vendorID}-${colIndex}`}
            className={classes.cell}
            style={{ width: col?.width }}
          >
            {renderCell(col, rowData, item)}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return <>{data.map((item) => renderServiceRows(item))}</>;
};
